import { createFileRoute } from '@tanstack/react-router';
import { CodeBlock, registerHighlighter } from '@osd/ui/code-block';
import { Section } from '@osd/ui/section';
import { Stack } from '@osd/ui/stack';
import { Text } from '@osd/ui/text';
import { SUPPORTED_LANGUAGES, highlightToHtml } from '../lib/highlight';

/**
 * /ui/code-block — live docs for the `<CodeBlock>` primitive.
 *
 * `<CodeBlock>` is the shared chrome every `/ui/*` page uses to
 * render syntax-highlighted copy-pasteable snippets. Lives at
 * `@osd/ui/code-block` (added 2026-09-22 — see root/osd#117).
 * Highlighting is provided by a pluggable highlighter that apps
 * register at boot via `registerHighlighter`; this app uses
 * Shiki (see `apps/web/src/lib/highlight.ts`).
 *
 * This page demonstrates the supported prop combinations so docs
 * authors can copy the right pattern when adding new pages.
 */
export const Route = createFileRoute('/ui/_layout/code-block')({
  component: CodeBlockPage,
});

// Register the Shiki-backed highlighter on mount. The registration
// is global, so we just call it here (no effect needed — the
// singleton is what matters).
registerHighlighter(async (code, lang) => {
  return highlightToHtml(code, lang as Parameters<typeof highlightToHtml>[1]);
});

// ── Sample code (module-level so the JSX stays readable) ─────
//
// We pull these out as constants because the JSX-expression form
// `{`...`}` for prop values can confuse the TSX parser when the
// string itself contains a literal `</CodeBlock>`. Stashing the
// strings here also makes them easier to grep / diff.

const INLINE_STRING_EXAMPLE = `<CodeBlock
  language="tsx"
  code={String.raw\`<Button variant="primary">Save</Button>\`}
/>`;

const CHILDREN_FORM_EXAMPLE = `<CodeBlock language="tsx">
  {\`<Button variant="primary">Save</Button>\`}
</CodeBlock>`;

const TITLE_HINT_EXAMPLE = `<Button variant="primary" style={{ width: '100%' }}>
  Save changes
</Button>`;

const TSX_SAMPLE = `import { useState } from 'react';

function Counter() {
  const [n, setN] = useState(0);
  return <button onClick={() => setN(n + 1)}>{n}</button>;
}`;

const TS_SAMPLE = `type User = { id: string; name: string };
function greet(u: User): string {
  return \`Hello, \${u.name}!\`;
}`;

const BASH_SAMPLE = `pnpm install --frozen-lockfile
pnpm type-check
pnpm test
pnpm build`;

const JSON_SAMPLE = `{
  "name": "@osd/web",
  "private": true,
  "type": "module",
  "scripts": {
    "dev": "vite --port 5173 --strictPort"
  }
}`;

const SQL_SAMPLE = `SELECT id, name, email, role, status
FROM users
WHERE status = 'approved'
  AND deleted_at IS NULL
ORDER BY created_at DESC
LIMIT 50;`;

const HTML_SAMPLE = `<button class="osd-button osd-button--primary" data-variant="primary">
  Save
</button>`;

const CSS_SAMPLE = `.osd-alert {
  padding: var(--osd-space-3) var(--osd-space-4);
  border: 1px solid var(--osd-color-border);
  border-radius: var(--osd-radius-sm);
}`;

function CodeBlockPage() {
  return (
    <Stack gap={6}>
      <Section title="CodeBlock" hint="syntax-highlighted snippet + copy button">
        <Stack gap={3}>
          <Text>
            Every docs page on this site uses <code>{'<CodeBlock>'}</code> for its copy-pasteable
            snippets. The component highlights the code via Shiki (async) and renders a plain
            monospace fallback in the same surface while it loads, so the page never flashes
            unstyled.
          </Text>
          <Text muted>
            Lives at <code>apps/web/src/components/docs.tsx</code> — web-local helper, not exported
            from <code>@osd/ui</code>. Docs authors import it from <code>../components/docs</code>{' '}
            alongside <code>{'<Section>'}</code>.
          </Text>
        </Stack>
      </Section>

      <Section title="Basic usage" hint="required props: `code` (or children), `language`">
        <Stack gap={5}>
          <CodeBlock
            title="Inline string (template literal)"
            language="tsx"
            code={INLINE_STRING_EXAMPLE}
          />

          <CodeBlock
            title="Children form (cleaner for backticks)"
            hint="pass the code as a child string — no nested-template escaping"
            language="tsx"
          >
            {CHILDREN_FORM_EXAMPLE}
          </CodeBlock>
        </Stack>
      </Section>

      <Section title="With title + hint" hint="optional header above the surface">
        <CodeBlock
          title="Hero CTA"
          hint="primary variant, full-width on mobile"
          language="tsx"
          code={TITLE_HINT_EXAMPLE}
        />
      </Section>

      <Section title="Supported languages" hint="each grammar is a separate browser chunk">
        <Stack gap={3}>
          <Text>
            Shiki ships the grammars as dynamic ESM imports — adding a new language is one entry in{' '}
            <code>SUPPORTED_LANGUAGES</code> and a dynamic-import fn in <code>highlight.ts</code>.
            Currently:
          </Text>
          <Text>
            <code>{SUPPORTED_LANGUAGES.join(', ')}</code>
          </Text>
        </Stack>
      </Section>

      <Section title="One block per language" hint="syntax highlighting samples">
        <Stack gap={5}>
          <CodeBlock title="tsx" language="tsx" code={TSX_SAMPLE} />
          <CodeBlock title="ts" language="ts" code={TS_SAMPLE} />
          <CodeBlock title="bash" language="bash" code={BASH_SAMPLE} />
          <CodeBlock title="json" language="json" code={JSON_SAMPLE} />
          <CodeBlock title="sql" language="sql" code={SQL_SAMPLE} />
          <CodeBlock title="html" language="html" code={HTML_SAMPLE} />
          <CodeBlock title="css" language="css" code={CSS_SAMPLE} />
        </Stack>
      </Section>

      <Section title="Copy button" hint="every block has a Copy → clipboard button">
        <Stack gap={3}>
          <Text>
            The Copy button in the top-right corner writes the raw <code>code</code> string (not the
            highlighted HTML) to the clipboard. State transitions: <code>Copy → Copied → Copy</code>{' '}
            (2 s timeout). On failure (e.g. insecure-context pages), the label switches to{' '}
            <code>Copy failed</code> and an ARIA-live announcement fires so screen readers hear the
            outcome.
          </Text>
          <Text muted>
            The button uses the same <code>useClipboardCopy</code> hook that the icon catalog tiles
            use. See <code>docs.test.tsx</code> for the test coverage (clipboard write + label
            transitions).
          </Text>
        </Stack>
      </Section>
    </Stack>
  );
}
